import { useEffect, useState, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { TOKEN_MINT } from '../config/constants';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface PriceData {
  mint: string;
  price: number; // Price per token in USD (primary)
  priceSol?: number; // Price per token in SOL (secondary)
  priceUsd: number; // Alias for price
  solUsdRate?: number; // SOL/USD conversion rate
  supply: number;
  marketCap: number; // Market cap in USD only
  decimals: number;
}

export function PriceDisplay() {
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [loading, setLoading] = useState(true);
  const wsUrl = API_URL.replace('http://', 'ws://').replace('https://', 'wss://') + '/api/data';
  const { lastMessage, isConnected, subscribeTokenTrade } = useWebSocket(wsUrl);

  // Retry logic for price fetch
  const fetchPrice = useCallback(async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(`${API_URL}/api/price`);
        if (res.ok) {
          const data = await res.json();
          setPriceData(data);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error(`Error fetching price (attempt ${i + 1}):`, error);
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
        }
      }
    }
    setLoading(false);
  }, [API_URL]);

  useEffect(() => {
    // Fetch initial price with retry logic
    fetchPrice();

    // Subscribe to token trade updates
    subscribeTokenTrade([TOKEN_MINT]);
  }, [fetchPrice, subscribeTokenTrade]);

  useEffect(() => {
    // Update price when WebSocket message received
    if (lastMessage?.type === 'trade') {
      // Update price based on trade data
      // This would need to be implemented based on actual trade data structure
    }
  }, [lastMessage]);

  if (loading) {
    return <div className="price-loading">Loading price...</div>;
  }

  if (!priceData) {
    return <div className="price-error">Failed to load price</div>;
  }

  return (
    <div className="price-display">
      <div className="price-main">
        <span className="price-label">Price:</span>
        <span className="price-value">${priceData.price.toFixed(6)}</span>
      </div>
      <div className="price-details">
        {priceData.priceSol && (
          <div>SOL: {priceData.priceSol.toFixed(8)} SOL</div>
        )}
        <div>Market Cap: ${priceData.marketCap.toLocaleString(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}</div>
        <div>Supply: {priceData.supply.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
      </div>
      <div className="ws-status">
        {isConnected ? '🟢 Live' : '🔴 Disconnected'}
      </div>
    </div>
  );
}

